import { useNavigate } from 'react-router-dom'
import type { User } from '../types'
import { DOVOLENA_MODULE_TITLE } from '../lib/dovolenaRequestType'
import {
  canActAsDovolenaPersonalista,
  canActAsDovolenaSpravceRozvrhu,
  canActAsDovolenaVedouci,
  canActAsDovolenaZadatel,
  isDovolenaItAdmin,
  labelDovolenaUiRole,
  type DovolenaUiRole,
  userCanSelectDovolenaRole,
} from '../lib/dovolenaRoles'
import './RoleSelector.css'

interface DovolenaRoleSelectorProps {
  onRoleSelect: (role: DovolenaUiRole) => void
  currentUser?: User
}

function DovolenaRoleSelector({ onRoleSelect, currentUser }: DovolenaRoleSelectorProps) {
  const navigate = useNavigate()
  const isITAdmin = isDovolenaItAdmin(currentUser)

  const canBeZadatel = canActAsDovolenaZadatel(currentUser)
  const canBeVedouci = canActAsDovolenaVedouci(currentUser)
  const canBePersonalista = canActAsDovolenaPersonalista(currentUser)
  const canBeSpravceRozvrhu = canActAsDovolenaSpravceRozvrhu(currentUser)

  const handleRoleClick = (role: DovolenaUiRole) => {
    if (!isITAdmin && !userCanSelectDovolenaRole(currentUser, role)) {
      alert(
        `Nemáte oprávnění k přístupu jako ${labelDovolenaUiRole(role)}. Kontaktujte IT administrátora.`,
      )
      return
    }
    onRoleSelect(role)
  }

  const roleButton = (
    role: DovolenaUiRole,
    title: string,
    description: string,
    allowed: boolean,
  ) => (
    <button
      type="button"
      className={`role-button ${!allowed && !isITAdmin ? 'disabled' : ''}`}
      onClick={() => handleRoleClick(role)}
      disabled={!allowed && !isITAdmin}
      title={!allowed && !isITAdmin ? `Nemáte oprávnění jako ${title}` : ''}
    >
      <h2>{title}</h2>
      <p>{description}</p>
      {!allowed && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
    </button>
  )

  return (
    <div className="role-selector">
      <div className="role-selector-container">
        <button
          type="button"
          className="back-to-roles-button"
          onClick={() => navigate('/intranet')}
          style={{ marginBottom: '1rem' }}
        >
          ← Zpět
        </button>
        <h1>{DOVOLENA_MODULE_TITLE}</h1>
        <p className="subtitle">Vyberte svou roli pro přístup k systému</p>

        <div className="role-buttons">
          {roleButton(
            'zadatel',
            'Žadatel',
            'Vlastní žádosti o nepřítomnost',
            canBeZadatel,
          )}
          {roleButton(
            'vedouci',
            'Vedoucí',
            'Schvalování žádostí a podání za zaměstnance',
            canBeVedouci,
          )}
          {roleButton(
            'personalista',
            'Personalista',
            'Přehled všech žádostí a evidence zpracování',
            canBePersonalista,
          )}
          {roleButton(
            'spravce_rozvrhu',
            'Správce rozvrhu',
            'Žádosti s potřebou suplování (záskok výuky)',
            canBeSpravceRozvrhu,
          )}
          {isITAdmin &&
            roleButton(
              'nastaveni',
              'Nastavení',
              'E-mail pro suplování a konfigurace vedoucích v žádostech',
              true,
            )}
        </div>

        {currentUser && (
          <div
            style={{
              marginTop: '2rem',
              padding: '1rem 1.5rem',
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              borderRadius: '12px',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500 }}>
              <strong>{currentUser.name}</strong>
              {currentUser.admin_type && (
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                  •{' '}
                  {currentUser.admin_type === 'spravce_it' ? 'Správce IT' : 'Správce Budov'}
                </span>
              )}
              {!currentUser.admin_type && currentUser.role !== 'admin' && (
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                  • {currentUser.role}
                </span>
              )}
              {isITAdmin && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', opacity: 0.95 }}>
                  ✅ Přístup ke všem rolím
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DovolenaRoleSelector
